import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  ArrowLeft, Camera, CameraOff, CheckCircle2, Clock, Download, FileText,
  Loader2, AlertTriangle, LifeBuoy, Target, ListChecks,
} from "lucide-react";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;
const HB_INTERVAL = 8000;
const DETECT_INTERVAL = 700;
const VIDEO_POSTER = "https://static.prod-images.emergentagent.com/jobs/b32a9d06-cd1a-4936-8a74-34f0d65c7ad3/images/1a255298060793ab976f59b49b4354e1c66f7605d796a28c312b834fcf7b0403.jpeg";

export default function ModuloPlayer({ modulo, tolleranza = 15, onBack, onModuleUpdate }) {
  const videoRef = useRef(null);
  const webcamRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const sessionRef = useRef(null);
  const lastFaceSeenRef = useRef(0);
  const stateRef = useRef({ playing: false, cameraActive: false, faceCounted: false, ended: false });

  const [sessionId, setSessionId] = useState(null);
  const [camState, setCamState] = useState("richiesta"); // richiesta | attiva | negata | errore
  const [faceRaw, setFaceRaw] = useState(false);
  const [faceCounted, setFaceCounted] = useState(false);
  const [detectorReady, setDetectorReady] = useState(false);
  const [detectorFallback, setDetectorFallback] = useState(false);
  const [progress, setProgress] = useState({ video_pct: modulo.video_pct || 0, presenza_pct: modulo.presenza_pct || 0, stato: modulo.stato, video_completed: modulo.video_completed });
  const [errMsg, setErrMsg] = useState(null);
  const [verificaSent, setVerificaSent] = useState(false);
  const [sendingVerifica, setSendingVerifica] = useState(false);
  const [started, setStarted] = useState(false);

  // ---- avvio sessione ----
  useEffect(() => {
    let cancelled = false;
    axios.post(`${API}/api/formazione/sessione/start`, { modulo_id: modulo.id }, { withCredentials: true })
      .then((r) => { if (!cancelled) { setSessionId(r.data.session_id); sessionRef.current = r.data.session_id; } })
      .catch((e) => setErrMsg(e.response?.data?.detail || "Sessione interrotta — riprova."));
    return () => {
      cancelled = true;
      const sid = sessionRef.current;
      if (sid) {
        axios.post(`${API}/api/formazione/sessione/${sid}/end`,
          { video_pos: videoRef.current?.currentTime || 0, ended: stateRef.current.ended },
          { withCredentials: true }).catch(() => {});
      }
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modulo.id]);

  // ---- webcam ----
  useEffect(() => {
    let cancelled = false;
    navigator.mediaDevices.getUserMedia({ video: { width: 320, height: 240 }, audio: false })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (webcamRef.current) webcamRef.current.srcObject = stream;
        setCamState("attiva");
        stateRef.current.cameraActive = true;
        stream.getVideoTracks()[0].addEventListener("ended", () => {
          setCamState("errore");
          stateRef.current.cameraActive = false;
        });
      })
      .catch((e) => {
        setCamState(e.name === "NotAllowedError" ? "negata" : "errore");
        stateRef.current.cameraActive = false;
      });
    return () => { cancelled = true; };
  }, []);

  // ---- face detector (MediaPipe) ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { FaceDetector, FilesetResolver } = await import("@mediapipe/tasks-vision");
        const vision = await FilesetResolver.forVisionTasks("https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm");
        const det = await FaceDetector.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: "https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite",
          },
          runningMode: "VIDEO",
          minDetectionConfidence: 0.4,
        });
        if (cancelled) { det.close(); return; }
        detectorRef.current = det;
        setDetectorReady(true);
      } catch (e) {
        console.warn("Face detector non disponibile, fallback camera-only", e);
        setDetectorFallback(true);
      }
    })();
    return () => { cancelled = true; if (detectorRef.current) detectorRef.current.close(); };
  }, []);

  // ---- loop di rilevamento volto con tolleranza ----
  useEffect(() => {
    const id = setInterval(() => {
      const camOn = stateRef.current.cameraActive;
      if (!camOn) { setFaceRaw(false); setFaceCounted(false); stateRef.current.faceCounted = false; return; }
      let present;
      if (detectorFallback || !detectorRef.current || !webcamRef.current || webcamRef.current.readyState < 2) {
        present = detectorFallback; // fallback: cam attiva = presenza
      } else {
        try {
          const res = detectorRef.current.detectForVideo(webcamRef.current, performance.now());
          present = (res.detections || []).length > 0;
        } catch (_e) { present = false; }
      }
      const now = Date.now();
      if (present) lastFaceSeenRef.current = now;
      const counted = present || (now - lastFaceSeenRef.current < tolleranza * 1000 && lastFaceSeenRef.current > 0);
      setFaceRaw(present);
      setFaceCounted(counted);
      stateRef.current.faceCounted = counted;
    }, DETECT_INTERVAL);
    return () => clearInterval(id);
  }, [detectorFallback, tolleranza]);

  // ---- heartbeat ----
  const sendHeartbeat = useCallback(async (extra = {}) => {
    const sid = sessionRef.current;
    if (!sid) return;
    try {
      const r = await axios.post(`${API}/api/formazione/sessione/${sid}/heartbeat`, {
        video_pos: videoRef.current?.currentTime || 0,
        playing: stateRef.current.playing,
        camera_active: stateRef.current.cameraActive,
        face_present: stateRef.current.faceCounted,
        ended: stateRef.current.ended,
        ...extra,
      }, { withCredentials: true });
      setProgress(r.data);
      if (r.data.stato === "completato" && progress.stato !== "completato") onModuleUpdate?.();
    } catch (_e) { /* connessione instabile: il prossimo heartbeat recupera */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!sessionId) return undefined;
    const id = setInterval(sendHeartbeat, HB_INTERVAL);
    return () => clearInterval(id);
  }, [sessionId, sendHeartbeat]);

  const onEnded = () => {
    stateRef.current.playing = false;
    stateRef.current.ended = true;
    sendHeartbeat({ ended: true, playing: false });
  };

  const scaricaPdf = async () => {
    try {
      const r = await axios.post(`${API}/api/formazione/moduli/${modulo.id}/pdf-download`, {}, { withCredentials: true });
      const dl = await axios.get(`${API}/api/me/corso-abilitazione/materiali/${r.data.materiale_id}/download`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(dl.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dispensa_${modulo.numero_label.replace(/[^a-z0-9]+/gi, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.response?.data?.detail || "Download non disponibile");
    }
  };

  const richiediVerifica = async (tipo) => {
    setSendingVerifica(true);
    try {
      await axios.post(`${API}/api/formazione/richiesta-verifica`, {
        modulo_id: modulo.id, tipo,
        messaggio: tipo === "presenza" ? `Presenza rilevata ${progress.presenza_pct}% — richiesta verifica manuale.` : "Richiesta assistenza tecnica dal modulo.",
      }, { withCredentials: true });
      setVerificaSent(true);
    } catch (_e) { alert("Invio non riuscito, riprova."); }
    finally { setSendingVerifica(false); }
  };

  const completato = progress.stato === "completato";
  const presenzaBassa = progress.video_completed && !completato && progress.presenza_pct < modulo.soglia_presenza;

  return (
    <div className="space-y-5" data-testid="modulo-player">
      <button onClick={onBack} data-testid="modulo-back-btn" className="inline-flex items-center gap-2 text-[13px] font-bold text-[#102a6b] hover:underline">
        <ArrowLeft className="h-4 w-4" /> Torna al percorso formativo
      </button>

      <div>
        <div className="text-[11px] uppercase tracking-wider font-extrabold text-slate-500">{modulo.numero_label}</div>
        <h2 className="text-xl md:text-2xl font-extrabold text-[#0a1f55]">{modulo.titolo}</h2>
        <div className="text-[13px] text-slate-500 mt-0.5 flex items-center gap-3">
          <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Durata video: {Math.round(modulo.durata_sec / 60)} minuti</span>
          <span className="inline-flex items-center gap-1"><Camera className="h-3.5 w-3.5" /> Presenza richiesta: ≥{modulo.soglia_presenza}%</span>
        </div>
      </div>

      {errMsg && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700 text-[13px] flex items-center gap-2" data-testid="modulo-error">
          <AlertTriangle className="h-4 w-4 shrink-0" /> {errMsg}
        </div>
      )}

      {/* AREA A (video 70%) + AREA B (presenza 30%) */}
      <div className="grid lg:grid-cols-[7fr_3fr] gap-5">
        <div className="rounded-2xl overflow-hidden bg-black border border-slate-200 relative">
          <video
            ref={videoRef}
            src={modulo.video_url}
            poster={VIDEO_POSTER}
            preload="metadata"
            controls
            controlsList="nodownload"
            playsInline
            className="w-full aspect-video bg-black"
            data-testid="modulo-video"
            onPlay={() => { setStarted(true); stateRef.current.playing = true; sendHeartbeat({ playing: true }); }}
            onPause={() => { stateRef.current.playing = false; sendHeartbeat({ playing: false }); }}
            onEnded={onEnded}
            onRateChange={(e) => { if (e.target.playbackRate !== 1) e.target.playbackRate = 1; }}
          />
          {!started && (
            <button
              data-testid="video-start-overlay"
              onClick={() => videoRef.current?.play()}
              className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-t from-black/60 via-transparent to-transparent cursor-pointer group"
            >
              <span className="h-16 w-16 rounded-full bg-[#f59e0b] text-[#0a1f55] flex items-center justify-center shadow-2xl transition-transform group-hover:scale-110">
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-7 w-7 ml-1"><path d="M8 5v14l11-7z" /></svg>
              </span>
              <span className="text-white font-extrabold text-[14px] drop-shadow">▶ Avvia la videolezione</span>
            </button>
          )}
        </div>

        {/* VERIFICA PRESENZA */}
        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="verifica-presenza-panel">
            <div className="text-[11px] uppercase tracking-wider font-extrabold text-slate-500 mb-2">Verifica presenza</div>
            <div className="rounded-xl overflow-hidden bg-slate-900 aspect-[4/3] relative">
              <video ref={webcamRef} autoPlay muted playsInline className="w-full h-full object-cover" />
              {camState !== "attiva" && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4 bg-slate-900/90 text-white">
                  <CameraOff className="h-7 w-7 text-red-400 mb-2" />
                  <div className="text-[12.5px] font-bold">
                    {camState === "richiesta" && "Autorizza la videocamera per la verifica presenza"}
                    {camState === "negata" && "Autorizzazione videocamera negata"}
                    {camState === "errore" && "Videocamera non disponibile"}
                  </div>
                </div>
              )}
            </div>
            <div className="mt-3 space-y-1.5 text-[12.5px] font-bold">
              <div className={`flex items-center gap-2 ${camState === "attiva" ? "text-emerald-700" : "text-red-600"}`} data-testid="camera-status">
                {camState === "attiva" ? <Camera className="h-4 w-4" /> : <CameraOff className="h-4 w-4" />}
                {camState === "attiva" ? "📷 Videocamera attiva" : "⚠️ Videocamera disattivata — il tempo di presenza non viene conteggiato"}
              </div>
              {camState === "attiva" && (
                <div className={`flex items-center gap-2 ${faceCounted ? "text-emerald-700" : "text-red-600"}`} data-testid="face-status">
                  <span className={`h-2.5 w-2.5 rounded-full ${faceCounted ? "bg-emerald-500" : "bg-red-500"} ${faceRaw ? "" : "animate-pulse"}`} />
                  {faceCounted ? "🟢 PRESENZA RILEVATA" : "🔴 PRESENZA NON RILEVATA"}
                </div>
              )}
              {camState === "attiva" && !faceRaw && faceCounted && (
                <div className="text-[11px] text-amber-600 font-semibold">Volto momentaneamente non rilevato — tolleranza {tolleranza}s attiva</div>
              )}
              {detectorFallback && camState === "attiva" && (
                <div className="text-[11px] text-slate-500 font-semibold">Rilevamento volto non disponibile su questo dispositivo: la presenza è basata sulla videocamera attiva.</div>
              )}
              {!detectorReady && !detectorFallback && camState === "attiva" && (
                <div className="text-[11px] text-slate-400 font-semibold flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Avvio rilevamento volto…</div>
              )}
            </div>
          </div>

          {/* Progresso */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="progresso-panel">
            <div>
              <div className="flex items-center justify-between text-[11px] uppercase tracking-wider font-extrabold text-slate-500 mb-1">
                <span>Video visualizzato</span><span className="text-[#102a6b]" data-testid="video-pct">{progress.video_pct}%</span>
              </div>
              <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full bg-[#102a6b] transition-all" style={{ width: `${progress.video_pct}%` }} />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between text-[11px] uppercase tracking-wider font-extrabold text-slate-500 mb-1">
                <span>Presenza modulo</span><span className={progress.presenza_pct >= modulo.soglia_presenza ? "text-emerald-600" : "text-[#b45309]"} data-testid="presenza-pct">{progress.presenza_pct}%</span>
              </div>
              <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                <div className={`h-full rounded-full transition-all ${progress.presenza_pct >= modulo.soglia_presenza ? "bg-emerald-500" : "bg-[#f59e0b]"}`} style={{ width: `${progress.presenza_pct}%` }} />
              </div>
              <div className="text-[11px] text-slate-500 mt-1">Minimo richiesto: {modulo.soglia_presenza}%</div>
            </div>
            {completato && (
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-emerald-700 text-[13px] font-bold flex items-center gap-2" data-testid="modulo-completato-banner">
                <CheckCircle2 className="h-4 w-4" /> ✅ MODULO COMPLETATO
              </div>
            )}
            {completato && (
              <Button onClick={onBack} data-testid="vai-modulo-successivo-btn" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
                Vai al modulo successivo →
              </Button>
            )}
            {presenzaBassa && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-[12.5px] text-amber-800" data-testid="presenza-insufficiente-banner">
                <div className="font-extrabold flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" /> Requisito di presenza non raggiunto</div>
                <div className="mt-1">Presenza rilevata: <strong>{progress.presenza_pct}%</strong> · Minima richiesta: <strong>{modulo.soglia_presenza}%</strong>. Puoi ripetere la sessione formativa oppure richiedere una verifica.</div>
              </div>
            )}
            {verificaSent ? (
              <div className="text-[12px] text-emerald-700 font-bold text-center">Richiesta inviata alla segreteria ✓</div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" size="sm" disabled={sendingVerifica} onClick={() => richiediVerifica("presenza")} data-testid="richiedi-verifica-btn" className="text-[11.5px] font-bold border-slate-300">
                  Richiedi verifica presenza
                </Button>
                <Button variant="outline" size="sm" disabled={sendingVerifica} onClick={() => richiediVerifica("assistenza")} data-testid="richiedi-assistenza-btn" className="text-[11.5px] font-bold border-slate-300">
                  <LifeBuoy className="h-3.5 w-3.5 mr-1" /> Assistenza
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* MATERIALI DEL MODULO */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
        <h3 className="font-extrabold text-[#0a1f55] flex items-center gap-2"><FileText className="h-5 w-5 text-[#f59e0b]" /> Materiali del modulo</h3>
        <p className="text-[13.5px] text-slate-600 whitespace-pre-line leading-relaxed">{modulo.descrizione}</p>
        {modulo.argomenti?.length > 0 && (
          <div>
            <div className="font-bold text-[#0a1f55] text-[13.5px] flex items-center gap-1.5 mb-2"><ListChecks className="h-4 w-4 text-[#f59e0b]" /> Argomenti trattati</div>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-[12.5px] text-slate-600 list-disc pl-5">
              {modulo.argomenti.map((a) => <li key={a}>{a}</li>)}
            </ul>
          </div>
        )}
        {modulo.obiettivi?.length > 0 && (
          <div>
            <div className="font-bold text-[#0a1f55] text-[13.5px] flex items-center gap-1.5 mb-2"><Target className="h-4 w-4 text-[#f59e0b]" /> Obiettivi formativi</div>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-[12.5px] text-slate-600 list-disc pl-5">
              {modulo.obiettivi.map((o) => <li key={o}>{o}</li>)}
            </ul>
          </div>
        )}
        {modulo.pdf_materiale_id && (
          <Button onClick={scaricaPdf} data-testid="scarica-dispensa-btn" className="bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold">
            <Download className="mr-2 h-4 w-4" /> 📄 Scarica dispensa PDF
          </Button>
        )}
      </div>
    </div>
  );
}
